import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useId, useState, useMemo } from 'react';
import classNames from 'classnames';
import { Highlight, themes, Prism } from 'prism-react-renderer';
import { ScrollArea } from '../ScrollArea/ScrollArea.es.js';
import { PrismLanguageMapping } from './CodeSnippet.Context/context.es.js';
export { SupportedLanguages } from './CodeSnippet.Context/context.es.js';
import { CodeSnippetProvider } from './CodeSnippet.Context/provider.es.js';
import { CodeSnippetControls } from './CodeSnippet.Controls/CodeSnippet.Controls.es.js';
import { CodeSnippetExpand } from './CodeSnippet.Expand/CodeSnippet.Expand.es.js';

// Corresponds to `line-height: 1.75rem` (28px) from typography-code plugin
const LINE_HEIGHT_PX = 28;
// Corresponds to `pt-6` (24px) on the `<pre>` element
const CONTAINER_PADDING_PX = 24;
/**
 * Component to render a code block with syntax highlight.
 * This component will render the code content as-is, and will not perform any code formatting.
 */ const CodeSnippet = ({ className, content, expanded = false, label, language, lines, locale = {
    showLess: 'Show Less',
    showMore: 'Show More',
    ariaLabel: 'Copy Text'
}, numbers = true, children })=>{
    const ariaId = useId();
    const [condensed, setCondensed] = useState(!expanded && lines !== 1);
    // Use inline styles for dynamic height since JIT can't detect runtime-generated class names
    const scrollAreaStyle = useMemo(()=>({
            height: condensed && lines != null ? `${lines * LINE_HEIGHT_PX + CONTAINER_PADDING_PX}px` : 'auto'
        }), [
        condensed,
        lines
    ]);
    // Conditional wrapper for ScrollArea
    const ConditionalScrollWrapper = ({ children })=>{
        if (condensed && lines != null) {
            return jsx(ScrollArea, {
                className: "scroll-area",
                "data-testid": "scroll-area",
                style: scrollAreaStyle,
                children: children
            });
        }
        return jsx(Fragment, {
            children: children
        });
    };
    // Maps a SupportedLanguages value to a valid Prism language identifier.
    const mappedLanguage = useMemo(()=>{
        return PrismLanguageMapping[language] || 'markup';
    }, [
        language
    ]);
    return jsxs("div", {
        className: classNames('cb-code-snippet', {
            'one-line': lines === 1
        }, className),
        "data-testid": "code-snippet",
        children: [
            label && jsx("div", {
                className: "code-snippet-label",
                id: `code-snippet-label-${ariaId}`,
                children: label
            }),
            jsx(Highlight, {
                code: content,
                language: mappedLanguage,
                prism: Prism,
                theme: themes.vsLight,
                children: ({ className, style, tokens, getLineProps, getTokenProps })=>jsx(CodeSnippetProvider, {
                        condensed: condensed,
                        content: content,
                        lines: lines ?? Number.MAX_VALUE,
                        locale: locale,
                        setCondensed: setCondensed,
                        tokens: tokens,
                        children: jsxs("div", {
                            className: "code-snippet-container",
                            children: [
                                jsx(ConditionalScrollWrapper, {
                                    children: jsx("pre", {
                                        "aria-labelledby": label && `code-snippet-label-${ariaId}`,
                                        className: classNames(className),
                                        "data-testid": "code-snippet-pre",
                                        style: style,
                                        children: jsx("div", {
                                            className: "code-box",
                                            children: jsx("code", {
                                                children: tokens.flatMap((line, i)=>jsxs("div", {
                                                        ...getLineProps({
                                                            line
                                                        }),
                                                        children: [
                                                            numbers && lines !== 1 && jsx("span", {
                                                                className: "line-number",
                                                                "data-testid": i,
                                                                children: i + 1
                                                            }),
                                                            jsx("span", {
                                                                children: line.map((token, key)=>jsx("span", {
                                                                        ...getTokenProps({
                                                                            token
                                                                        })
                                                                    }, key))
                                                            })
                                                        ]
                                                    }, i))
                                            })
                                        })
                                    })
                                }),
                                children,
                                jsx(CodeSnippetExpand, {})
                            ]
                        })
                    })
            })
        ]
    });
};
CodeSnippet.displayName = 'CodeSnippet';
CodeSnippet.Controls = CodeSnippetControls;

export { CodeSnippet, PrismLanguageMapping, CodeSnippet as default };
//# sourceMappingURL=CodeSnippet.es.js.map
